import { useQuery } from '@tanstack/react-query';
import { MessageType } from '@/types/system-message-templates';
import { getAccessToken } from '@/lib/supabase';
import { useDebounce } from './useDebounce';

interface PreviewParams {
  messageType: MessageType;
  template_content: string;
  enabled?: boolean;
}

async function fetchPreview(messageType: MessageType, template_content: string): Promise<string> {
  const token = await getAccessToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`/api/system-message-templates/${messageType}/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ template_content }),
  });

  if (!response.ok) {
    throw new Error('Failed to generate preview');
  }

  const data = await response.json();
  return data.preview;
}

export function useSystemMessagePreview({ messageType, template_content, enabled = true }: PreviewParams) {
  // Debounce the template content to avoid excessive API calls
  const debouncedContent = useDebounce(template_content, 300);

  return useQuery({
    queryKey: ['system-message-preview', messageType, debouncedContent],
    queryFn: () => fetchPreview(messageType, debouncedContent),
    enabled: enabled && !!debouncedContent,
    staleTime: 60000, // Cache preview for 1 minute
  });
}
